/**
 * Port Type System
 *
 * Defines all valid port types, their visual styling, compatibility rules,
 * and human-readable labels. Used for type-safe connections between nodes.
 */

// Built-in port types. Structured node-schema ports use concrete schema type
// names (for example DialogueEntryDict) and are intentionally kept as raw
// strings for exact-match compatibility checks.
export type PortType =
  // Primitives
  | 'int'
  | 'float'
  | 'string'
  | 'boolean'
  // Media
  | 'image'
  | 'video'
  | 'audio'
  // Lists
  | 'int[]'
  | 'float[]'
  | 'string[]'
  | 'boolean[]'
  | 'image[]'
  | 'video[]'
  | 'audio[]'

type PortTypeVisualKey = PortType | 'structured'

// Legacy type mappings (for backward compatibility with existing schemas)
export const LEGACY_TYPE_MAP: Record<string, string> = {
  text: 'string',
  textarea: 'string',
  number: 'int',
  file: 'image',
  select: 'string',
}

const BUILT_IN_PORT_TYPES: ReadonlySet<string> = new Set([
  'int',
  'float',
  'string',
  'boolean',
  'image',
  'video',
  'audio',
  'int[]',
  'float[]',
  'string[]',
  'boolean[]',
  'image[]',
  'video[]',
  'audio[]',
])

/**
 * Normalize legacy type names to their modern equivalents.
 * Handles legacy types like 'text', 'object', etc.
 * Unknown types pass through unchanged.
 */
export function normalizePortType(type: string): string {
  return LEGACY_TYPE_MAP[type] ?? type
}

function isBuiltInPortType(type: string): type is PortType {
  return BUILT_IN_PORT_TYPES.has(type)
}

function getPortTypeVisualKey(type: string): PortTypeVisualKey {
  const normalized = normalizePortType(type)
  return isBuiltInPortType(normalized) ? normalized : 'structured'
}

/**
 * Port type Tailwind CSS classes for visual distinction
 */
export const PORT_TYPE_COLORS: Record<PortTypeVisualKey, { bg: string; border: string; text: string }> = {
  // Primitives - cool colors
  int: { bg: 'bg-blue-500/20', border: 'border-blue-400', text: 'text-blue-300' },
  float: { bg: 'bg-cyan-500/20', border: 'border-cyan-400', text: 'text-cyan-300' },
  string: { bg: 'bg-green-500/20', border: 'border-green-400', text: 'text-green-300' },
  boolean: { bg: 'bg-yellow-500/20', border: 'border-yellow-400', text: 'text-yellow-300' },

  // Media - warm colors
  image: { bg: 'bg-purple-500/20', border: 'border-purple-400', text: 'text-purple-300' },
  video: { bg: 'bg-pink-500/20', border: 'border-pink-400', text: 'text-pink-300' },
  audio: { bg: 'bg-rose-500/20', border: 'border-rose-400', text: 'text-rose-300' },

  // Lists - brighter versions
  'int[]': { bg: 'bg-blue-600/20', border: 'border-blue-500', text: 'text-blue-200' },
  'float[]': { bg: 'bg-cyan-400/20', border: 'border-cyan-300', text: 'text-cyan-200' },
  'string[]': { bg: 'bg-green-600/20', border: 'border-green-500', text: 'text-green-200' },
  'boolean[]': { bg: 'bg-yellow-400/20', border: 'border-yellow-300', text: 'text-yellow-200' },
  'image[]': { bg: 'bg-purple-600/20', border: 'border-purple-500', text: 'text-purple-200' },
  'video[]': { bg: 'bg-pink-600/20', border: 'border-pink-500', text: 'text-pink-200' },
  'audio[]': { bg: 'bg-rose-600/20', border: 'border-rose-500', text: 'text-rose-200' },

  // Structured named schema types - neutral
  structured: { bg: 'bg-orange-500/20', border: 'border-orange-400', text: 'text-orange-300' },
}

/**
 * Get the Tailwind CSS color classes for a port type.
 * Falls back to the structured color for named schema types.
 */
export function getPortTypeColor(type: string): { bg: string; border: string; text: string } {
  return PORT_TYPE_COLORS[getPortTypeVisualKey(type)]
}

/**
 * Hex color assignments per port type, used for handle rendering.
 */
const PORT_TYPE_HEX_COLORS: Record<PortTypeVisualKey, string> = {
  // Primitives
  int: '#3b82f6',        // blue-500
  float: '#06b6d4',      // cyan-500
  string: '#22c55e',     // green-500
  boolean: '#eab308',    // yellow-500

  // Media
  image: '#a855f7',      // purple-500
  video: '#ec4899',      // pink-500
  audio: '#f43f5e',      // rose-500

  // Lists - brighter
  'int[]': '#60a5fa',    // blue-400
  'float[]': '#22d3ee',  // cyan-400
  'string[]': '#4ade80', // green-400
  'boolean[]': '#facc15', // yellow-400
  'image[]': '#c084fc',  // purple-400
  'video[]': '#f472b6',  // pink-400
  'audio[]': '#fb7185',  // rose-400

  // Structured
  structured: '#f97316', // orange-500
}

/**
 * Get the hex color for a port type.
 * Falls back to the structured color (orange, #f97316) for named schema types.
 */
export function getPortTypeHexColor(portType: PortType | string): string {
  return PORT_TYPE_HEX_COLORS[getPortTypeVisualKey(portType)]
}

/**
 * Check if two port types are compatible for connection.
 * Source (output) -> Target (input).
 * Both types are normalized before comparison.
 *
 * Rules (evaluated in order):
 * 1. Exact match -> compatible
 * 2. int -> float widening -> compatible
 * 3. Scalar -> corresponding array promotion -> compatible
 * 4. Everything else -> incompatible
 */
export function areTypesCompatible(sourceType: string, targetType: string): boolean {
  const source = normalizePortType(sourceType)
  const target = normalizePortType(targetType)

  // Schema-declared union inputs accept each constituent media type.
  if (target.includes('|')) {
    return source.split('|').every(member => target.split('|').some(option => areTypesCompatible(member, option)))
  }

  // Rule 1: Exact match
  if (source === target) {
    return true
  }

  // Rule 2: int can connect to float (widening)
  if (source === 'int' && target === 'float') {
    return true
  }

  // Rule 3: Single item can connect to array of same type
  // e.g., image -> image[]
  if (target.endsWith('[]') && source === target.slice(0, -2)) {
    return true
  }

  return false
}

/**
 * Human-readable labels for port types.
 */
const PORT_TYPE_LABELS: Record<PortType, string> = {
  int: 'Integer',
  float: 'Number',
  string: 'Text',
  boolean: 'Boolean',
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  'int[]': 'Int List',
  'float[]': 'Float List',
  'string[]': 'Text List',
  'boolean[]': 'Boolean List',
  'image[]': 'Image List',
  'video[]': 'Video List',
  'audio[]': 'Audio List',
}

/**
 * Get a human-readable label for a port type.
 * Returns the raw normalized type string for unknown types.
 */
export function getPortTypeLabel(type: string): string {
  const normalized = normalizePortType(type)
  return isBuiltInPortType(normalized) ? PORT_TYPE_LABELS[normalized] : normalized
}

/**
 * react-i18next label keys (under the `canvas` namespace) for the human-facing
 * port-type labels. The port-type *value* (`int`, `image[]`, `DialogueEntryDict`
 * …) stays a fixed identifier used for compatibility checks and edge typing;
 * only the tooltip *label* is localized — mirroring the built-in style-name /
 * style-group pattern (`localizeStyleName` / `localizeStyleGroup`). Keys use
 * camelCase slugs since i18n keys can't carry the `[]` list suffix. A missing
 * entry (structured/unknown types) falls through to the raw English label.
 */
const PORT_TYPE_LABEL_KEYS: Record<PortType, string> = {
  int: 'portTypeLabels.integer',
  float: 'portTypeLabels.number',
  string: 'portTypeLabels.text',
  boolean: 'portTypeLabels.boolean',
  image: 'portTypeLabels.image',
  video: 'portTypeLabels.video',
  audio: 'portTypeLabels.audio',
  'int[]': 'portTypeLabels.intList',
  'float[]': 'portTypeLabels.numberList',
  'string[]': 'portTypeLabels.textList',
  'boolean[]': 'portTypeLabels.booleanList',
  'image[]': 'portTypeLabels.imageList',
  'video[]': 'portTypeLabels.videoList',
  'audio[]': 'portTypeLabels.audioList',
}

/**
 * Resolve a port type to its react-i18next label key under the `canvas`
 * namespace, or `undefined` for structured/unknown types (which render their
 * raw type name verbatim). See `localizePortTypeLabel`.
 */
export function portTypeLabelKey(type: string): string | undefined {
  const normalized = normalizePortType(type)
  return isBuiltInPortType(normalized) ? PORT_TYPE_LABEL_KEYS[normalized] : undefined
}

/**
 * Localize a port-type *display* label (canvas port hover tooltips). Built-in
 * types resolve to a localized label; structured/unknown types return their raw
 * type name unchanged (structural passthrough guard). `getPortTypeLabel`
 * supplies the English `defaultValue` so a missing locale key never blanks the
 * tooltip.
 *
 * @param type - The port type (built-in `PortType` or a structured schema name).
 * @param t - A react-i18next `TFunction`; the key resolves under `canvas`.
 */
export function localizePortTypeLabel(
  type: string,
  t: (key: string, opts?: { ns?: string; defaultValue?: string }) => string,
): string {
  const key = portTypeLabelKey(type)
  if (!key) return getPortTypeLabel(type)
  return t(key, { ns: 'canvas', defaultValue: getPortTypeLabel(type) })
}
